"use client";

// ── SiteLinkNavPaint — RUN:SNOWCLUB-SEO-CUTOVER-001 · card 86bbe7fty (W2a) ──────────────────────
//
// THE SAME BAND THE CRAWLER GETS, PUT WHERE THE VISITOR CAN SEE IT — without moving one pixel of
// the founder's design.
//
// THE PROBLEM, MEASURED BEFORE ANYTHING WAS WRITTEN. `SiteLinkNav` server-renders its <nav> in
// ordinary document flow, and W1's RAW no-JS sweep is GREEN 400/400 on that basis. But on 370 of
// the 400 contract paths the page mounts `ExportShell` (page.tsx) and `DesignSurface`
// (DesignSurface.tsx, its returned <div>), and EACH of those is `position:fixed; inset:0;
// background:#fff; z-index:2`. Two opaque fixed sheets take the whole viewport out of flow, so the
// document's own body ends up ~0–557px tall behind them and NOTHING in flow paints. Measured on
// this tree at 1440x900:
//     /                  body.scrollHeight 321   document.scrollHeight 900 == clientHeight 900
//     /mountain-whistler body.scrollHeight 557   document.scrollHeight 900 == clientHeight 900
// The band was at y=0, full width, with its 11 / 29 anchors — painted, and underneath an opaque
// sheet. The document does not scroll at all on these routes; the DESIGN scrolls, inside itself.
//
// SO THE BAND HAS TO BE INSIDE THE THING THAT SCROLLS. This clones the served node into the
// design's own footer at boot and hides the original. It does not restyle the design, does not
// reposition anything the design owns, and adds no box anywhere except one <nav> at the end of the
// design's own footer — which is where a site-links band belongs.
//
// ⛔ CORRECTED 2026-08-23 (founder QA Batch 1, item 3). Until this date the clone was appended to
// the SCROLL CONTAINER, i.e. AFTER the footer, and the rest of this header described that as the
// intended order. Measured on the deployed 23232878, both viewports, that shipped two stacked
// footer-shaped regions with duplicated links, and the founder called it what it is. The target is
// now the footer element itself. Any sentence below that still reads "after the footer" describes
// the OLD placement and is retained only where it explains a measurement taken at that time.
//
// ── WHY A CLONE AND NOT A MOVE ─────────────────────────────────────────────────────────────────
// The original stays in the DOM, exactly as served, because it is what the crawler reads and what
// the no-JS visitor gets. `display:none` is applied to it ONLY after the clone is in place and only
// on the client, so the delivered HTML is byte-identical either way. Hiding it is not cosmetic: two
// copies of 5,132 links would mean duplicate tab stops and, on a route where the design fails to
// paint, two visible bands. One copy is visible at any time, always.
//
// ── HOW "THE DESIGN IS UP" IS DECIDED — design-restore's predicate, not a second one ────────────
// `hasRenderedTemplate(resolveHost(null, document))` — the exported truth from ./design-restore,
// whose own header records why this is the only honest reading: `__SG_DESIGN_STATE === "up"` was
// measured (58 loads, held 8cac5cbb) to be set on a SYNCHRONOUS handoff to an ASYNCHRONOUS
// renderer, so it says "the runtime mounted", never "the design painted". `[data-dc-tpl]` is the
// design runtime's OWN marker on its OWN rendered nodes — a verifying read. This file imports that
// function rather than re-implementing it, so there is exactly one definition of "up" in the tree.
//
// ── HOW THE SCROLL CONTAINER IS FOUND, AND WHY THE OBVIOUS RULE IS WRONG ────────────────────────
// ⛔ "THE ELEMENT WITH THE LARGEST scrollHeight-clientHeight" IS NOT THE ANSWER, and it fails on a
// path in this very card's proof set. Measured, /trips at 1440x900:
//     d=559  div 418x664                     <- the mountain-picker panel. LARGEST. Wrong element.
//     d=147  main[My Wallet] 1440x824        <- the real scroll container, holds the footer
// The picker is a genuinely scrollable overlay list that simply has more overflow than a short
// screen's main. On /mountain-whistler/equipment-rentals at 390 the same rule is beaten by four
// 0x0 divs reporting d=1037. A rule that picks a floating panel would staple the site's link band
// to the inside of a dropdown.
//
// WHAT IS ACTUALLY TRUE, measured across 5 paths x 2 viewports (10/10, no exceptions): the design's
// own VISIBLE <footer data-screen-label="Landing — footer"> is the LAST ELEMENT CHILD of its
// parent, and that parent is the scrolling `<main>` (`overflow-y:auto`, `flex:1 1 0%`) —
//     /                     main 1440x900  d=7790   last child footer[Landing — footer]
//     /mountain-whistler    main 1440x824  d=5023   last child footer[Landing — footer]
//     .../bus-schedule/skylynx  main 1202x696  d=603    last child footer[Landing — footer]
//     .../equipment-rentals/black-tie  main  d=2039  last child footer[Landing — footer]
//     /trips                main[My Wallet] d=147    last child footer[Landing — footer]
// (and the same five at 390x844). That is not a coincidence — DesignSurface PUTS it there: its
// footer block inserts the design's own footer immediately before each screen main's `</main>`
// ("the footer belongs once, in normal document flow, AFTER all primary content", founder ruling 7).
//
// So the primary rule is the measured one — the design's own visible footer names its own
// container — and the largest-delta scan is kept as the FALLBACK for a screen that renders no
// footer, hardened with the three filters that would have excluded every wrong answer above:
// a real scroll overflow (`overflow-y: auto|scroll`), a visible box, and a tiebreak on VIEWPORT
// AREA rather than overflow depth. That container scan is now the FALLBACK only — it is used on a
// screen that renders no footer at all, where last-in-container is still after all content and
// there is no footer for the band to sit behind. Where a footer exists, the band goes inside it.
//
// ── WHY THE OBSERVER WATCHES THE HOST AND NOT THE CONTAINER ────────────────────────────────────
// MEASURED: an in-place screen switch can REPLACE the container. On
// /mountain-whistler/equipment-rentals/black-tie, clicking "Show all ›" moved the design from the
// supplier detail screen to the rentals index; the probe node stayed attached to the OLD main
// (still in the document) while a DIFFERENT element became the live container. An observer bound to
// the container would have watched a screen nobody is looking at. So the observer is bound to the
// fixed host — the one element that survives every in-place switch — and the container is
// RE-DISCOVERED on every tick.
//
// That same measurement is why the clone is MOVED rather than re-created: `appendChild` relocates
// the existing node, so a container swap cannot leave a copy behind on the old screen. A sweep for
// stray `[data-site-link-nav-paint]` nodes runs alongside it, so "exactly one painted copy" is
// enforced rather than assumed.
//
// EVERYTHING IS IN try/catch AND EVERY FAILURE PATH LEAVES THE ORIGINAL BAND EXACTLY AS SERVED —
// in flow, painted, crawlable. This component can only ever add a copy and hide the original after
// that copy is proven to be in the document; it can never subtract the band.

import { useEffect } from "react";
import { hasRenderedTemplate, resolveHost } from "./design-restore";

/** The clone's marker. The served copy keeps `data-site-link-nav`; the painted copy carries this
 *  and NOT that, so an instrument can count "served" and "painted" separately and a gate can tell
 *  which one it is looking at. */
const PAINT_ATTR = "data-site-link-nav-paint";

/**
 * Wait for the actual DesignSurface template without ever timing out a healthy late boot. The
 * caller's `start` returns true only after it has placed the clone, so the observer remains live
 * through placeholder and partial-template mutations and is always disconnectable on unmount.
 */
/** Bounded retry for a design that was already painted before the consumer's effect ran. */
const WAIT_RETRY_INTERVAL_MS = 250;
const WAIT_RETRY_WINDOW_MS = 15_000;

export function waitForRenderedTemplate(
  host: HTMLElement,
  isUp: () => boolean,
  start: () => boolean,
): () => void {
  let observer: MutationObserver | null = null;
  let timer: number | null = null;
  let stopped = false;
  const attempt = () => {
    if (stopped || !isUp() || !start()) return;
    stopped = true;
    observer?.disconnect();
    observer = null;
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };
  attempt();
  if (!stopped) {
    observer = new MutationObserver(attempt);
    observer.observe(host, { childList: true, subtree: true });
    // ── SNOWCLUB-LAUNCH-REMAINING-PARITY-001 · ranked-route parity — THE WARM-VISIT GAP, MEASURED ──
    // On a SECOND visit in the same browser (design assets cached) the design is fully painted at
    // ~229 ms, before this effect runs. The first attempt then fails on a layout that has not settled,
    // and a design that is already complete emits no further mutation — so the observer above never
    // fires again and BOTH consumers of this waiter (the site link band and the content clone) stay
    // absent for the whole visit. Proven 2026-09-21 on the served build: appending one unrelated node
    // to the host made both appear at once. A bounded retry closes that window; it stops the moment
    // start() succeeds, at the window's end, or when the consumer unmounts.
    const startedAt = Date.now();
    const tick = () => {
      timer = null;
      if (stopped) return;
      attempt();
      if (!stopped && Date.now() - startedAt < WAIT_RETRY_WINDOW_MS) timer = window.setTimeout(tick, WAIT_RETRY_INTERVAL_MS);
    };
    timer = window.setTimeout(tick, WAIT_RETRY_INTERVAL_MS);
  }
  return () => {
    stopped = true;
    observer?.disconnect();
    observer = null;
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };
}

/** One re-append per this window, trailing edge. A screen switch produces hundreds of mutations;
 *  this collapses them into a single corrective append. */
const REAPPEND_DEBOUNCE_MS = 250;

/** A box has to overflow by more than this to count as a scroll container in the fallback scan. */
const SCROLLER_MIN_DELTA = 40;

export function isVisible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return false;
  const cs = getComputedStyle(el);
  return cs.display !== "none" && cs.visibility !== "hidden";
}

/** The design's own visible footer — the one whose parent the band belongs after. The LAST visible
 *  one, because DesignSurface inserts a copy into every screen main and only the live screen's is
 *  visible; taking the last is stable when a hidden screen still holds a laid-out copy. */
export function designFooter(host: Element): HTMLElement | null {
  const found = Array.from(host.querySelectorAll<HTMLElement>("footer")).filter(isVisible);
  return found.length ? found[found.length - 1]! : null;
}

/**
 * EXACTLY ONE `contentinfo`, and it is the footer the visitor can see.
 *
 * WHY THIS IS NEEDED AT ALL, measured on the deployed 23232878: every page checked reported
 * `document.querySelectorAll('[role="contentinfo"]').length === 0`. A bare `<footer>` is only
 * mapped to the `contentinfo` landmark when it is NOT scoped to a sectioning element, and the
 * design's footer is a child of the screen's `<main>` — so the site has a footer and no footer
 * landmark. The founder's ruling asks for exactly one, so it is stated explicitly here rather
 * than left to an implicit mapping that the markup already defeats.
 *
 * Idempotent, and it never creates a second one: any other element already claiming the role is
 * cleared first, so the count can only ever be 1 after this runs.
 */
function markContentinfo(footer: HTMLElement): void {
  for (const other of Array.from(document.querySelectorAll<HTMLElement>('[role="contentinfo"]'))) {
    if (other !== footer) other.removeAttribute("role");
  }
  if (footer.getAttribute("role") !== "contentinfo") footer.setAttribute("role", "contentinfo");
}

/**
 * The band was authored as a STANDALONE region — its own grey ground and its own top border — for
 * the placement it used to have, after the footer. Inside the footer that chrome is what makes it
 * read as a second footer stacked on the first, which is the defect. Strip only those two
 * declarations on the painted copy; the served copy, which is what a no-JS visitor sees in flow,
 * keeps its standalone chrome because there it genuinely is standalone.
 */
/**
 * The footer's LEGAL ROW — the © line carrying Privacy / Terms / Contact / FAQ.
 *
 * FOUNDER RULING 2026-08-23, second pass: "the legal row must be the final visible footer row."
 * The first repair moved this band INSIDE the footer, which answered the DOM-sibling question and
 * did NOT answer what he was looking at. Measured on deployed 3bb2836b, /mountain-whistler at 1440
 * and at 390, the footer's bands ran: mark → wordmark → BOOK/EXPLORE/COMPANY/SUPPORT → © row →
 * THIS BAND. A 61-link block under the copyright still reads as a second footer, and he said so
 * twice. I graded "no sibling after the footer" — the property I could measure — instead of
 * "nothing below the legal row", the property he named. Same failure the estate has banked before.
 *
 * FOUND BY CONTENT, NOT BY POSITION. The legal row is the last footer child in today's template,
 * but "last child" stops being true the moment this band is inserted, and a template edit could add
 * a row after it. The © / copyright text is what makes a row the legal row, so that is what is
 * matched. The clone is excluded from the search so a re-place cannot find itself.
 */
function legalRow(footer: HTMLElement, clone: HTMLElement | null): HTMLElement | null {
  const kids = Array.from(footer.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && el !== clone,
  );
  for (let i = kids.length - 1; i >= 0; i--) {
    const el = kids[i]!;
    if (/©|copyright/i.test(el.textContent || "")) return el;
  }
  return null;
}

function blendIntoFooter(clone: HTMLElement): void {
  clone.style.setProperty("background", "transparent", "important");
  clone.style.setProperty("border-top", "0", "important");
  clone.setAttribute("data-band-merged", "footer");
}

/**
 * ── QA-BATCH-01 row 3 (2026-08-27) · THE CLONE STOPS REPEATING THE FOOTER'S OWN LINKS ──────────
 *
 * FOUNDER INVENTORY, measured on 86485e82 at "/": 31 footer anchors, 10 URLs repeated — and every
 * repeat was this clone carrying a URL the design footer's own columns or legal row already show
 * (contact-us ×3, faq ×3, trips ×3, profile ×3, snowclub-perks/general-information/deals/
 * bus-schedule/terms/privacy ×2). The band was authored for a page where NOTHING else was visible;
 * inside the footer, its Explore group is a second copy of rows the footer already has.
 *
 * DEDUPLICATE, NEVER DELETE DESTINATIONS — the founder's own guard. An anchor is dropped from the
 * PAINTED CLONE only when the same normalized URL is already visible in this same footer outside
 * the clone, so every destination keeps exactly one visible anchor and the link GRAPH loses
 * nothing. The SERVED band is untouched: crawlers and no-JS visitors keep every anchor exactly as
 * delivered. A group whose links are all duplicates drops its heading too (an empty "Mountains"
 * heading is not navigation). Idempotent — removals are removals — and re-run on every re-place,
 * where it can only ever remove a dupe the previous pass could not see yet.
 */
function dedupeCloneAgainstFooter(footer: HTMLElement, clone: HTMLElement): void {
  const norm = (h: string): string => {
    const path = h.replace(/^https?:\/\/[^/]+/, "").split(/[?#]/)[0] ?? "";
    const trimmed = path.replace(/\/+$/, "");
    return trimmed || "/";
  };
  // INTERNAL LINKS ONLY, on both sides — measured defect from the first cut of this function: the
  // footer's external "About SnowGroup" (https://snowgroup.ca, no path) normalized to "/" and
  // silently deleted the band's "Home" anchor — and with it the whole Explore group — leaving "/"
  // with no visible home link at all (crawlable-links VISIBLE dropped 8→6 on "/"). An external
  // origin can never be the same destination as an internal path, so it never enters the baseline.
  const own = new Set<string>();
  for (const a of Array.from(footer.querySelectorAll<HTMLAnchorElement>("a[href]"))) {
    if (clone.contains(a)) continue;
    const href = a.getAttribute("href");
    if (href && href.startsWith("/")) own.add(norm(href));
  }
  if (own.size === 0) return; // a footer with no anchors of its own is not a dedupe baseline
  for (const a of Array.from(clone.querySelectorAll<HTMLAnchorElement>("a[href]"))) {
    const href = a.getAttribute("href");
    if (href && href.startsWith("/") && own.has(norm(href))) a.remove();
  }
  for (const group of Array.from(clone.querySelectorAll<HTMLElement>("[data-site-link-group]"))) {
    if (!group.querySelector("a[href]")) group.remove();
  }
  // FOUNDER RULING 2026-09-02 - "no one-item category". Deduplication can leave a group holding a
  // SINGLE row under its own heading: measured on /mountain-whistler/bus-schedule, "Popular routes"
  // came down to one, because the footer's own BOOK column already carries this mountain's
  // bus-schedule index. A heading over one link is not a category.
  //
  // THE SURVIVING ANCHOR IS NEVER DROPPED - the founder's "deduplicate, never delete destinations"
  // guard still holds, and the link graph loses nothing. Only the lone heading goes: the row is moved
  // into the neighbouring group's list. That is a real trade and it is stated rather than hidden - the
  // row sits under a heading that is not its own. It is the lesser of the two, because the alternative
  // the founder named is the one being removed. Runs after the empty-group sweep, so anything emptied
  // above is already gone, and never fires when there is no neighbour to fold into.
  const groups = Array.from(clone.querySelectorAll<HTMLElement>("[data-site-link-group]"));
  if (groups.length > 1) {
    for (const group of groups) {
      const links = Array.from(group.querySelectorAll<HTMLAnchorElement>("a[href]"));
      if (links.length !== 1) continue;
      const live = Array.from(clone.querySelectorAll<HTMLElement>("[data-site-link-group]"));
      const idx = live.indexOf(group);
      const target = live[idx + 1] ?? live[idx - 1];
      const list = target?.querySelector("a[href]")?.parentElement;
      if (!list) continue;
      list.appendChild(links[0]!);
      group.remove();
    }
  }
}

/**
 * The element the design actually scrolls. Primary: the design's own footer names its parent —
 * measured 10/10 above. Fallback: the largest genuinely-scrollable visible box, ranked by viewport
 * AREA (the tiebreak that beats /trips' picker panel) with overflow depth as the second key.
 */
export function findScrollContainer(host: Element): HTMLElement | null {
  const footer = designFooter(host);
  if (footer && footer.parentElement && footer.parentElement !== host) return footer.parentElement;

  let best: HTMLElement | null = null;
  let bestArea = -1;
  let bestDelta = -1;
  for (const el of Array.from(host.querySelectorAll<HTMLElement>("*"))) {
    const delta = el.scrollHeight - el.clientHeight;
    if (delta <= SCROLLER_MIN_DELTA) continue;
    const cs = getComputedStyle(el);
    if (cs.overflowY !== "auto" && cs.overflowY !== "scroll") continue;
    if (!isVisible(el)) continue;
    const area = el.clientWidth * el.clientHeight;
    if (area > bestArea || (area === bestArea && delta > bestDelta)) {
      best = el;
      bestArea = area;
      bestDelta = delta;
    }
  }
  return best;
}

export default function SiteLinkNavPaint({ setId }: { setId: string }) {
  useEffect(() => {
    // Everything this effect owns, so the cleanup can undo all of it whatever state it reached.
    let clone: HTMLElement | null = null;
    let observer: MutationObserver | null = null;
    let stopWatchingTemplate: (() => void) | null = null;
    let debounce: number | null = null;
    let disposed = false;

    /** The served band for THIS page. Scoped by set id as well as by attribute so that a soft
     *  navigation, during which the outgoing and incoming bands can both be in the document for a
     *  tick, can never bind this instance to the other route's node. `:not([...])` keeps it off a
     *  band another instance has already taken. */
    const findOriginal = (): HTMLElement | null =>
      document.querySelector<HTMLElement>(
        `[data-site-link-nav][data-site-link-set="${CSS.escape(setId)}"]:not([data-band-shadowed])`,
      ) ?? document.querySelector<HTMLElement>(`[data-site-link-nav][data-site-link-set="${CSS.escape(setId)}"]`);

    /** Exactly one painted copy in the document, and it is ours. A container swap on an in-place
     *  screen switch is the case this exists for. */
    const sweepStrays = () => {
      for (const el of Array.from(document.querySelectorAll<HTMLElement>(`[${PAINT_ATTR}]`))) {
        if (el !== clone) el.remove();
      }
    };

    /** Put the clone where it belongs, if it is not already there. Returns true when, on return,
     *  the clone is the last element child of its target.
     *
     *  ── FOUNDER RULING 2026-08-23 · QA BATCH 1 ITEM 3 — THE TARGET IS NOW THE FOOTER ITSELF ───
     *  This used to append the clone to the SCROLL CONTAINER, which put it AFTER the design's own
     *  footer by construction — and the header above said so in as many words. Measured on the
     *  deployed 23232878 at 1440x900 and 390x844, that produced two footer-shaped regions in a row
     *  on /mountain-whistler: <footer data-screen-label="Landing — footer"> ending at y=5923, then
     *  this band — 61 links, full width, grey ground, its own top border — from y=5923 to y=6794,
     *  duplicating EXPLORE / Contact us / FAQ / General information out of the footer directly
     *  above it. The founder read it as a second footer, which is what it is; the ruling is one
     *  footer, one contentinfo, and nothing painted after the footer.
     *
     *  THE FIX IS THE TARGET, NOT THE STYLING. Appending INSIDE the design's own <footer> makes the
     *  band part of the single footer region instead of a sibling after it. Nothing else changes:
     *  the served band is still the delivered HTML a crawler reads, the clone still carries every
     *  anchor, and no link is dropped to make a count pass.
     *
     *  The scroll container is still resolved, because it is what the FALLBACK needs on a screen
     *  that renders no footer at all — there the band stays last-in-container, which is still after
     *  all content and is still the only region of its kind on that screen. */
    const place = (): boolean => {
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      if (!host || !clone) return false;
      const footer = designFooter(host);
      // PRIMARY: inside the design's own footer, IMMEDIATELY BEFORE THE LEGAL ROW.
      // Founder ruling: the legal row is the final visible footer row. Appending as the footer's
      // last child put this band under the copyright, which is what he saw and called a second
      // footer — twice.
      if (footer) {
        const legal = legalRow(footer, clone);
        // `insertBefore`/`appendChild` MOVE an attached node — deliberate, and what makes a screen
        // swap safe: the clone cannot exist twice, because it is never constructed twice.
        if (legal) {
          if (legal.previousElementSibling !== clone) footer.insertBefore(clone, legal);
        } else if (footer.lastElementChild !== clone) {
          // No legal row on this screen's footer: last child is still after all footer content and
          // there is nothing for the band to sit under.
          footer.appendChild(clone);
        }
        markContentinfo(footer);
        blendIntoFooter(clone);
        // Row 3: one visible anchor per destination inside the one footer — see the function's
        // header for the measured inventory this answers. Runs on every (re-)place; idempotent.
        dedupeCloneAgainstFooter(footer, clone);
        sweepStrays();
        return legal ? legal.previousElementSibling === clone : footer.lastElementChild === clone;
      }
      // FALLBACK: a screen with no footer of its own. Last child of the scroll container is still
      // after all of that screen's content, and there is no footer for it to sit behind.
      const container = findScrollContainer(host);
      if (!container) return false;
      if (container.lastElementChild !== clone) container.appendChild(clone);
      sweepStrays();
      return container.lastElementChild === clone;
    };

    /** Build the clone from the served band and get it into the design. Only on success is the
     *  original hidden — a half-done attempt must leave the served band painted. */
    const mount = (): boolean => {
      const original = findOriginal();
      if (!original) return false;
      if (clone && clone.isConnected) return place();

      const copy = original.cloneNode(true) as HTMLElement;
      // Served vs painted must be distinguishable by an instrument that only reads attributes.
      copy.removeAttribute("data-site-link-nav");
      copy.setAttribute(PAINT_ATTR, "");
      // The clone is not React's and not the design runtime's; say so, so nothing else adopts it.
      copy.setAttribute("data-sg-cloned", "1");
      clone = copy;

      if (!place()) {
        clone = null;
        return false;
      }

      // ⛔ `style.display = "none"` DOES NOT WORK HERE and the reason is in SiteLinkNav's own CSS:
      // `[data-site-link-nav]{display:block!important; …}` — a deliberate floor so a captured
      // stylesheet cannot collapse the band. An ordinary inline declaration loses to it. Inline
      // `!important` is the only thing that wins, so it is what is used.
      original.style.setProperty("display", "none", "important");
      original.setAttribute("aria-hidden", "true");
      original.setAttribute("data-band-shadowed", "1");
      return true;
    };

    /** Undo everything, in the order that leaves the served band correct at every intermediate
     *  step: the original is un-hidden BEFORE the clone goes, so there is never a moment with no
     *  band at all. */
    const teardown = () => {
      try {
        const original = document.querySelector<HTMLElement>(
          `[data-site-link-nav][data-band-shadowed][data-site-link-set="${CSS.escape(setId)}"]`,
        );
        if (original) {
          original.style.removeProperty("display");
          original.removeAttribute("aria-hidden");
          original.removeAttribute("data-band-shadowed");
        }
      } catch { /* the band is React's; if it is already gone there is nothing to restore */ }
      try { if (clone) clone.remove(); } catch { /* already detached */ }
      clone = null;
      try { observer?.disconnect(); } catch { /* already disconnected */ }
      observer = null;
      try { stopWatchingTemplate?.(); } catch { /* already disconnected */ }
      stopWatchingTemplate = null;
      if (debounce !== null) { window.clearTimeout(debounce); debounce = null; }
    };

    /** The design is up once its own runtime has rendered at least one of its own template nodes.
     *  design-restore owns this predicate; this file does not restate it. */
    const designIsUp = (): boolean => {
      try { return hasRenderedTemplate(resolveHost(null, document)); } catch { return false; }
    };

    const start = (): boolean => {
      if (disposed) return false;
      if (!mount()) return false;

      // The host survives every in-place screen switch; the container does not. See the header.
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      if (!host) return false;
      observer = new MutationObserver(() => {
        if (disposed || debounce !== null) return;
        debounce = window.setTimeout(() => {
          debounce = null;
          if (disposed) return;
          // `place()` is a no-op when the clone is already the container's last child, so the
          // mutations this very call produces cannot drive a second one.
          try { place(); } catch { /* a failed re-append leaves the previous placement alone */ }
        }, REAPPEND_DEBOUNCE_MS);
      });
      observer.observe(host, { childList: true, subtree: true });
      return true;
    };

    try {
      // DesignSurface can legitimately paint after its recovery window. Install this observer
      // before trying to mount: the previous bounded poll gave up before the placement observer
      // existed, leaving the served band as a second public footer forever on a late healthy boot.
      // On routes with no design it simply observes an otherwise inert host; the served SSR band
      // remains visible and crawlable. It disconnects only after `mount()` has placed the clone.
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      const tryStart = (): boolean => {
        if (disposed || !designIsUp()) return false;
        return start();
      };
      if (host) stopWatchingTemplate = waitForRenderedTemplate(host, designIsUp, tryStart);
    } catch {
      // Any failure at all leaves the served band exactly as delivered.
    }

    return () => {
      disposed = true;
      teardown();
    };
  }, [setId]);

  return null;
}
